import React from "react";
import { shape, func, bool } from "prop-types";
import { useSelector } from "react-redux";
import GridTable from "pages/_components/GridTable/GridTable";
import CustomText from "pages/_components/CustomText/CustomText";
import FormattedDate from "pages/_components/FormattedDate";
import { echeqPropTypes } from "pages/echeck/echeckPropTypes";
import { emittedEcheqsStatusActions, statusTagColor } from "util/echecks";
import CustomButton from "pages/_components/CustomButton/CustomButton";
import FormattedAmount from "pages/_components/FormattedAmount";
import Image from "pages/_components/Image";
import useActionByEcheckState from "pages/echeck/hooks/useActionByEcheckState";
import InfoTag from "pages/echeck/components/InfoTag";
import CustomData from "pages/_components/CustomData/CustomData";

const EmitedEcheckItem = ({ isDesktop, emitededEcheck, setDrawer }) => {
    const { options, isExpired } = useActionByEcheckState(emittedEcheqsStatusActions, emitededEcheck, setDrawer);
    const { environmentCuit } = useSelector((state) => state.echecks);
    const estado = emitededEcheck.estado.split("-");

    return (
        <>
            <GridTable.Data className="align-items-center" columnStart={1} rowStart={1} rowWidth={1}>
                {isDesktop ? (
                    <div className="flex flex-nowrap">
                        <CustomText text={emitededEcheck.cheque_numero} textType="TEXT" />
                        {emitededEcheck.tenencia_beneficiario_documento === environmentCuit && (
                            <div className="align-start">
                                <Image src="images/ownershipEcheqs.svg" />
                            </div>
                        )}
                    </div>
                ) : (
                    <CustomData
                        titleText="echecks.number"
                        titleTextType="I18N"
                        valueText={emitededEcheck.cheque_numero}
                        valueTextType="TEXT"
                        icon={
                            emitededEcheck.tenencia_beneficiario_documento === environmentCuit &&
                            "images/ownershipEcheqs.svg"
                        }
                    />
                )}
            </GridTable.Data>
            <GridTable.Data
                className="align-items-center"
                alignX={isDesktop ? "center" : "start"}
                columnStart={isDesktop ? 2 : 1}
                rowStart={isDesktop ? 1 : 2}
                rowWidth={1}>
                {isDesktop ? (
                    <CustomText
                        text={emitededEcheck.beneficiario_nombre}
                        textType="TEXT"
                        className="capitalize wrap"
                        toLower
                    />
                ) : (
                    <CustomData
                        titleText="echecks.beneficiary"
                        titleTextType="I18N"
                        valueText={emitededEcheck.beneficiario_nombre}
                        valueTextType="TEXT"
                    />
                )}
            </GridTable.Data>
            <GridTable.Data
                className="align-items-center"
                columnStart={isDesktop ? 3 : 1}
                rowStart={isDesktop ? 1 : 3}
                rowWidth={1}
                alignX="center">
                {isDesktop ? (
                    <CustomText
                        text={emitededEcheck.beneficiario_documento_tipo.concat(
                            " ",
                            emitededEcheck.beneficiario_documento,
                        )}
                        textType="TEXT"
                        className="data-cuit"
                    />
                ) : (
                    <CustomData
                        titleText="echecks.document"
                        titleTextType="I18N"
                        valueText={emitededEcheck.beneficiario_documento_tipo.concat(
                            " ",
                            emitededEcheck.beneficiario_documento,
                        )}
                        valueTextType="TEXT"
                        valueClass="data-cuit"
                    />
                )}
            </GridTable.Data>
            <GridTable.Data
                className="align-items-center"
                columnStart={isDesktop ? 4 : 1}
                rowStart={isDesktop ? 1 : 4}
                rowWidth={1}
                alignX={isDesktop ? "center" : "start"}>
                {isDesktop ? (
                    <CustomText text={emitededEcheck.moneda} textType="TEXT" />
                ) : (
                    <CustomData
                        titleText="echecks.currency"
                        titleTextType="I18N"
                        valueText={emitededEcheck.moneda}
                        valueTextType="TEXT"
                    />
                )}
            </GridTable.Data>
            <GridTable.Data
                className="align-items-center"
                columnStart={isDesktop ? 5 : 1}
                rowStart={isDesktop ? 1 : 5}
                rowWidth={1}
                alignX="center">
                {isDesktop ? (
                    <FormattedAmount currency={emitededEcheck.emisor_moneda} quantity={emitededEcheck.monto} />
                ) : (
                    <CustomData
                        titleText="echecks.amount"
                        titleTextType="I18N"
                        valueText={
                            <FormattedAmount currency={emitededEcheck.emisor_moneda} quantity={emitededEcheck.monto} />
                        }
                        valueTextType="TEXT"
                    />
                )}
            </GridTable.Data>
            <GridTable.Data
                className="align-items-center"
                columnStart={isDesktop ? 6 : 1}
                rowStart={isDesktop ? 1 : 6}
                rowWidth={1}
                alignX="center">
                {isDesktop ? (
                    <FormattedDate showTime={false} date={emitededEcheck.fecha_emision} />
                ) : (
                    <CustomData
                        titleText="echecks.dateEmited"
                        titleTextType="I18N"
                        valueText={<FormattedDate showTime={false} date={emitededEcheck.fecha_emision} />}
                        valueTextType="TEXT"
                    />
                )}
            </GridTable.Data>
            <GridTable.Data
                className="align-items-center"
                columnStart={isDesktop ? 7 : 1}
                rowStart={isDesktop ? 1 : 7}
                rowWidth={1}
                alignX="center">
                {isDesktop ? (
                    <FormattedDate showTime={false} date={emitededEcheck.fecha_pago} />
                ) : (
                    <CustomData
                        titleText="echecks.datePayed"
                        titleTextType="I18N"
                        valueText={<FormattedDate showTime={false} date={emitededEcheck.fecha_pago} />}
                        valueTextType="TEXT"
                    />
                )}
            </GridTable.Data>
            <GridTable.Data
                className="tags-wrapper"
                columnStart={isDesktop ? 8 : 2}
                rowStart={1}
                rowWidth={1}
                alignX={isDesktop ? "center" : "end"}
                alignY="start">
                {estado.map((tag) => (
                    <InfoTag type="info" message={tag} tagBackground={statusTagColor(emitededEcheck.estado)} />
                ))}
            </GridTable.Data>
            <GridTable.Data
                className="dropdown menu"
                columnStart={isDesktop ? 9 : 3}
                rowStart={1}
                rowWidth={1}
                alignX="center"
                alignY="start">
                {options.length > 0 && !isExpired && (
                    <CustomButton pullRight icon="images/figma/icon/menu-mobile.svg" options={options} />
                )}
            </GridTable.Data>
        </>
    );
};

EmitedEcheckItem.propTypes = {
    emitededEcheck: shape(echeqPropTypes).isRequired,
    setDrawer: func.isRequired,
    isDesktop: bool.isRequired,
};

export default EmitedEcheckItem;
